/**
 * Composant Statut de la partie
 */

import React, { useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { useGame } from '../hooks/useGame';
import { socketService } from '../services/socketService';
import { soundService } from '../services/soundService';

export const GameStatus: React.FC = () => {
  const {
    gameState,
    playerId,
    isMyTurn,
    placingWall,
    wallOrientation,
    myPlayer,
    togglePlacingWall,
    toggleWallOrientation,
  } = useGame();

  const error = useGameStore((state) => state.error);
  const endMessage = useGameStore((state) => state.endMessage);
  const view = useGameStore((state) => state.view);
  const isVsAI = useGameStore((state) => state.isVsAI);
  const gameStartTime = useGameStore((state) => state.gameStartTime);
  const moveHistory = useGameStore((state) => state.moveHistory);
  const [soundOn, setSoundOn] = useState(soundService.isEnabled());

  // Colonnes en lettres (a-i)
  const colLetter = (col: number) => String.fromCharCode(97 + col);
  const formatMove = (idx: number, action: typeof moveHistory[0]) => {
    const prefix = `${idx + 1}. J${action.playerId}`;
    if (action.type === 'move' && action.to) {
      return `${prefix} → ${colLetter(action.to.col)}${action.to.row + 1}`;
    } else if (action.type === 'wall' && action.wall) {
      const o = action.wall.orientation === 'horizontal' ? 'H' : 'V';
      return `${prefix} 🧱${o} ${colLetter(action.wall.col)}${action.wall.row + 1}`;
    }
    return prefix;
  };

  if (!gameState) return null;

  const playerName = useGameStore((state) => state.playerName);

  const toggleSound = () => {
    const newState = !soundOn;
    soundService.setEnabled(newState);
    setSoundOn(newState);
  };

  const handleLeave = () => {
    if (view === 'playing') {
      if (!window.confirm('Abandonner la partie ? Vous perdrez la partie.')) return;
    }
    socketService.leaveRoom();
  };

  const handleRematch = () => {
    const timeLimit = gameState.turnTimeLimit;
    const wasVsAI = isVsAI;
    socketService.leaveRoom();
    setTimeout(() => {
      if (wasVsAI) {
        socketService.createRoomVsAI(playerName, timeLimit);
      } else {
        socketService.createRoom(playerName, timeLimit);
      }
    }, 100);
  };

  return (
    <div className="card space-y-4">
      {/* Message d'état */}
      <div className="text-center">
        {view === 'finished' ? (
          <div className="space-y-3">
            <div
              className={`text-xl font-bold ${
                gameState.winner === playerId ? 'text-green-400' : 'text-red-400'
              }`}
            >
              {gameState.winner === playerId ? '🏆 Victoire !' : '😞 Défaite...'}
            </div>
            {endMessage && (
              <p className="text-gray-400 text-sm">{endMessage}</p>
            )}
            {/* Statistiques de la partie */}
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 bg-gray-900 rounded-lg p-2">
              <div>
                <span className="block text-gray-400">Coups joués</span>
                <span className="text-white font-mono">{gameState.turnNumber}</span>
              </div>
              {gameStartTime && (
                <div>
                  <span className="block text-gray-400">Durée</span>
                  <span className="text-white font-mono">
                    {Math.floor((Date.now() - gameStartTime) / 60000)}m
                    {Math.floor(((Date.now() - gameStartTime) % 60000) / 1000)}s
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={handleRematch}
              className="btn btn-primary w-full"
            >
              Revanche
            </button>
            <button
              onClick={handleLeave}
              className="btn btn-secondary w-full"
            >
              Retour au lobby
            </button>
          </div>
        ) : (
          <div
            className={`text-lg font-semibold ${
              isMyTurn ? 'text-green-400' : 'text-gray-400'
            }`}
          >
            {isMyTurn
              ? 'À vous de jouer !'
              : isVsAI && gameState.currentPlayer !== playerId
                ? "🤖 L'IA réfléchit..."
                : "Tour de l'adversaire..."}
          </div>
        )}
      </div>

      {/* Erreur */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg px-4 py-2 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Contrôles */}
      {view === 'playing' && isMyTurn && (
        <div className="space-y-3">
          {/* Toggle placement mur */}
          <button
            onClick={togglePlacingWall}
            disabled={!myPlayer || myPlayer.wallsRemaining <= 0}
            className={`btn w-full ${
              placingWall ? 'bg-orange-500 text-white' : 'btn-secondary'
            }`}
          >
            {placingWall ? '🧱 Mode Mur (actif)' : '🧱 Placer un mur'}
            {myPlayer && myPlayer.wallsRemaining > 0 && (
              <span className="ml-2 text-xs opacity-70">[W]</span>
            )}
          </button>

          {/* Orientation du mur */}
          {placingWall && (
            <button
              onClick={toggleWallOrientation}
              className="btn btn-secondary w-full text-sm"
            >
              Orientation: {wallOrientation === 'horizontal' ? '─ Horizontal' : '│ Vertical'}
              <span className="ml-2 text-xs opacity-70">[R]</span>
            </button>
          )}

          {/* Instructions */}
          <p className="text-gray-500 text-xs text-center">
            {placingWall
              ? 'Cliquez / flèches pour placer le mur · Échap pour annuler'
              : 'Cliquez ou utilisez les flèches pour vous déplacer'}
          </p>
        </div>
      )}

      {/* Historique des coups */}
      {view === 'playing' && moveHistory.length > 0 && (
        <div className="bg-gray-900 rounded-lg p-2 max-h-32 overflow-y-auto">
          <p className="text-gray-500 text-xs mb-1">Historique</p>
          <div className="space-y-0.5">
            {[...moveHistory].reverse().map((action, i) => (
              <div key={i} className={`text-xs font-mono ${i === 0 ? 'text-yellow-400' : 'text-gray-500'}`}>
                {formatMove(moveHistory.length - 1 - i, action)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bouton son + quitter */}
      {view === 'playing' && (
        <div className="flex gap-2">
          <button
            onClick={toggleSound}
            className="btn btn-secondary text-sm px-3"
            title={soundOn ? 'Couper le son' : 'Activer le son'}
          >
            {soundOn ? '🔊' : '🔇'}
          </button>
          <button
            onClick={handleLeave}
            className="btn btn-secondary w-full text-sm opacity-60 hover:opacity-100"
          >
            Abandonner
          </button>
        </div>
      )}
    </div>
  );
};
